import { getErrorCode, getErrorMessage } from '@/lib/error-utils';
import type {
  FrameImage,
  MentionToken,
  ReferenceImage,
  SubmitReferenceResourceType,
  VideoGenerationMode,
} from '@/types/conversation-input';
import {
  GenerationReferenceRole,
  type GenerationReferenceItem,
} from '@/types/generation-reference';

import type { LocalGenerationReferenceToken } from './generation-reference-draft-reconcile';
import { parseGenerationReferenceIdentity } from './generation-reference-draft-reconcile';
import { buildComposerGenerationReferences } from './generation-composer-references';
import {
  buildGenerationReferenceIdentityKey,
  buildGenerationResourceKey,
} from './generation-reference';

export const UNAVAILABLE_GENERATION_REFERENCE_MESSAGE_PREFIX = '引用资源不存在或不可用';

type SubmitReferenceImageItem = {
  role?: GenerationReferenceItem['role'];
  resource_id?: string;
  resource_type?: SubmitReferenceResourceType;
};

export interface SubmitReferenceGuardContext {
  resourceKeys: string[];
  composerIntentSignature: string;
  unreadyResourceKeys: string[];
  /** 校验与草稿水合完成、构建最终 payload 时冻结的完整提交签名。 */
  submitIntentSignature?: string;
  /** 等待异步校验期间，确认输入框内容仍与点击时一致。 */
  isSubmitIntentCurrent?: () => boolean;
}

export type GenerationReferenceSyncErrorClassification =
  | {
      kind: 'unavailable';
      message: string;
      unavailableResourceKeys: ReadonlySet<string>;
    }
  | {
      kind: 'network' | 'terminal';
      message: string;
      unavailableResourceKeys: ReadonlySet<string>;
    };

function collectModeAwareFrameEntries(params: {
  videoMode?: VideoGenerationMode;
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
}): Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>> {
  const entries: Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>> = [];
  const { videoMode, firstFrame, lastFrame } = params;

  if (
    firstFrame?.resourceId &&
    firstFrame.resourceType &&
    (videoMode === 'first_frame' ||
      videoMode === 'first_frame_audio' ||
      videoMode === 'first_last_frame' ||
      videoMode === 'first_last_frame_audio')
  ) {
    entries.push({
      resourceType: firstFrame.resourceType,
      resourceId: firstFrame.resourceId,
      role: GenerationReferenceRole.FIRST_FRAME,
    });
  }

  if (
    lastFrame?.resourceId &&
    lastFrame.resourceType &&
    (videoMode === 'first_last_frame' || videoMode === 'first_last_frame_audio')
  ) {
    entries.push({
      resourceType: lastFrame.resourceType,
      resourceId: lastFrame.resourceId,
      role: GenerationReferenceRole.LAST_FRAME,
    });
  }

  return entries;
}

function collectTokenResourceKeys(
  tokens: readonly LocalGenerationReferenceToken[]
): ReadonlySet<string> {
  const resourceKeys = new Set<string>();
  for (const token of tokens) {
    const parsed = parseGenerationReferenceIdentity(token.identity);
    if (!parsed) continue;
    resourceKeys.add(buildGenerationResourceKey(parsed));
  }
  return resourceKeys;
}

function resolveReferenceAssetRole(
  reference: Pick<ReferenceImage, 'kind'>
): GenerationReferenceItem['role'] {
  return reference.kind === 'video'
    ? GenerationReferenceRole.REFERENCE_VIDEO
    : reference.kind === 'audio'
      ? GenerationReferenceRole.REFERENCE_AUDIO
      : GenerationReferenceRole.REFERENCE_IMAGE;
}

function collectReferenceAssetEntries(referenceImages: readonly ReferenceImage[]): {
  entries: Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>>;
  unreadyResourceKeys: string[];
} {
  const entries: Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>> = [];
  const unreadyResourceKeys: string[] = [];

  for (const referenceImage of referenceImages) {
    if (!referenceImage.resourceId || !referenceImage.resourceType) continue;
    const entry = {
      resourceType: referenceImage.resourceType,
      resourceId: referenceImage.resourceId,
      role: resolveReferenceAssetRole(referenceImage),
    } satisfies Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>;
    entries.push(entry);
    if (!referenceImage.remoteUrl) {
      unreadyResourceKeys.push(buildGenerationResourceKey(entry));
    }
  }

  return { entries, unreadyResourceKeys };
}

function collectVisibleMentionTokenEntries(
  mentionTokens: readonly MentionToken[],
  referenceImages: readonly ReferenceImage[]
): {
  entries: Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>>;
  unreadyResourceKeys: string[];
} {
  const entries: Array<Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>> = [];
  const unreadyResourceKeys: string[] = [];

  for (const token of mentionTokens) {
    if (
      token.kind !== 'resource' ||
      !(token.resource?.submitResourceId ?? token.resource?.resourceId) ||
      !token.resource.submitResourceType
    ) {
      if (token.kind !== 'reference-image' || !token.referenceImageId) {
        continue;
      }
      const referenceImage = referenceImages.find((item) => item.id === token.referenceImageId);
      if (!referenceImage?.resourceId || !referenceImage.resourceType) continue;
      const entry = {
        resourceType: referenceImage.resourceType,
        resourceId: referenceImage.resourceId,
        role: resolveReferenceAssetRole(referenceImage),
      } satisfies Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>;
      entries.push(entry);
      if (!referenceImage.remoteUrl) {
        unreadyResourceKeys.push(buildGenerationResourceKey(entry));
      }
      continue;
    }

    const entry = {
      resourceType: token.resource.submitResourceType,
      resourceId: token.resource.submitResourceId ?? token.resource.resourceId!,
      role: token.resource.generationReferenceRole ?? GenerationReferenceRole.REFERENCE_IMAGE,
    } satisfies Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>;
    entries.push(entry);

    if (!token.resource.resourceUrl) {
      unreadyResourceKeys.push(buildGenerationResourceKey(entry));
    }
  }

  return { entries, unreadyResourceKeys };
}

export function extractUnavailableGenerationResourceKeys(error: unknown): ReadonlySet<string> {
  const message = getErrorMessage(error, '');
  const match = message.match(/^引用资源不存在或不可用:\s*type=(\d+),\s*id=([0-9a-z-]+)/i);
  if (!match?.[1] || !match[2]) return new Set<string>();
  return new Set([`${Number(match[1])}:${match[2]}`]);
}

export function classifyGenerationReferenceSyncError(
  error: unknown
): GenerationReferenceSyncErrorClassification {
  const unavailableResourceKeys = extractUnavailableGenerationResourceKeys(error);
  const message = getErrorMessage(error, '引用同步失败，请稍后重试');
  if (unavailableResourceKeys.size > 0) {
    return { kind: 'unavailable', message, unavailableResourceKeys };
  }

  const code = getErrorCode(error);
  return {
    kind: code == null || code >= 500 ? 'network' : 'terminal',
    message,
    unavailableResourceKeys,
  };
}

export function hasUnavailableGenerationResourceKeys(
  frozenResourceKeys: ReadonlySet<string>,
  unavailableResourceKeys: ReadonlySet<string>
): boolean {
  for (const resourceKey of frozenResourceKeys) {
    if (unavailableResourceKeys.has(resourceKey)) return true;
  }
  return false;
}

export function buildVisibleGenerationResourceKeys(params: {
  localGenerationReferenceTokens: readonly LocalGenerationReferenceToken[];
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
  videoMode?: VideoGenerationMode;
}): ReadonlySet<string> {
  const resourceKeys = new Set(collectTokenResourceKeys(params.localGenerationReferenceTokens));
  for (const entry of collectModeAwareFrameEntries(params)) {
    resourceKeys.add(buildGenerationResourceKey(entry));
  }
  return resourceKeys;
}

export function buildGenerationReferenceIdentitySignature(
  references: readonly Pick<GenerationReferenceItem, 'resourceType' | 'resourceId' | 'role'>[]
): string {
  return [...new Set(references.map(buildGenerationReferenceIdentityKey))].sort().join('|');
}

export function buildComposerSubmitIntentSignature(params: {
  text: string;
  generationKind: 'image' | 'video' | 'sound' | 'text' | 'voice';
  videoMode?: VideoGenerationMode;
  mentionTokens: readonly MentionToken[];
  referenceImages: readonly ReferenceImage[];
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
  settings?: unknown;
}): string {
  const tokenSignatures = params.mentionTokens.map((token) => [
    token.kind,
    token.start,
    token.end,
    token.display,
    token.resource?.submitResourceType ?? null,
    token.resource?.submitResourceId ?? token.resource?.resourceId ?? null,
    token.resource?.generationReferenceRole ?? null,
    token.resource?.resourceUrl ?? null,
    token.referenceImageId ?? null,
    token.referenceImageUrl ?? null,
    (token.dialogueItems ?? []).map((item) => [item.dialogueId ?? null, item.content]),
    token.controlPayload ?? null,
    token.commandKey ?? null,
    token.commandMeta ?? null,
  ]);
  const referenceSignatures = params.referenceImages.map((reference) => [
    reference.id,
    reference.resourceType ?? null,
    reference.resourceId ?? reference.attachmentId ?? null,
    reference.kind ?? null,
    reference.remoteUrl,
    reference.url,
  ]);
  const frameSignature = (frame?: FrameImage | null) =>
    frame
      ? [
          frame.id,
          frame.resourceType ?? null,
          frame.resourceId ?? frame.attachmentId ?? null,
          frame.remoteUrl ?? null,
          frame.url,
        ]
      : null;

  return JSON.stringify([
    params.text,
    params.generationKind,
    params.videoMode ?? null,
    tokenSignatures,
    referenceSignatures,
    frameSignature(params.firstFrame),
    frameSignature(params.lastFrame),
    params.settings ?? null,
  ]);
}

export function buildSubmitReferenceGuardContext(params: {
  payloadReferenceImages?: readonly SubmitReferenceImageItem[];
  mentionTokens: readonly MentionToken[];
  referenceImages?: readonly ReferenceImage[];
  firstFrame?: FrameImage | null;
  lastFrame?: FrameImage | null;
  videoMode?: VideoGenerationMode;
}): SubmitReferenceGuardContext {
  const visibleMentionEntries = collectVisibleMentionTokenEntries(
    params.mentionTokens,
    params.referenceImages ?? []
  );
  const referenceAssetEntries = collectReferenceAssetEntries(params.referenceImages ?? []);
  const frameEntries = collectModeAwareFrameEntries(params);
  const frameRoles = new Set(frameEntries.map((entry) => entry.role));
  const composerIntentEntries = buildComposerGenerationReferences({
    mentionTokens: [...params.mentionTokens],
    referenceImages: [...(params.referenceImages ?? [])],
    firstFrame: frameRoles.has(GenerationReferenceRole.FIRST_FRAME) ? params.firstFrame : null,
    lastFrame: frameRoles.has(GenerationReferenceRole.LAST_FRAME) ? params.lastFrame : null,
    videoMode: params.videoMode,
  });
  const payloadEntries = (params.payloadReferenceImages ?? [])
    .filter(
      (
        item
      ): item is Required<
        Pick<SubmitReferenceImageItem, 'resource_id' | 'resource_type' | 'role'>
      > => Boolean(item.resource_id && item.resource_type && item.role)
    )
    .map((item) => ({
      resourceType: item.resource_type,
      resourceId: item.resource_id,
      role: item.role,
    }));

  const resourceKeys = new Set<string>();
  for (const entry of [
    ...visibleMentionEntries.entries,
    ...referenceAssetEntries.entries,
    ...frameEntries,
    ...payloadEntries,
  ]) {
    resourceKeys.add(buildGenerationResourceKey(entry));
  }
  for (const item of params.payloadReferenceImages ?? []) {
    if (!item.resource_id || !item.resource_type) continue;
    resourceKeys.add(
      buildGenerationResourceKey({
        resourceType: item.resource_type,
        resourceId: item.resource_id,
      })
    );
  }

  return {
    resourceKeys: [...resourceKeys].sort(),
    composerIntentSignature: buildGenerationReferenceIdentitySignature(composerIntentEntries),
    unreadyResourceKeys: [
      ...new Set([
        ...visibleMentionEntries.unreadyResourceKeys,
        ...referenceAssetEntries.unreadyResourceKeys,
      ]),
    ].sort(),
  };
}
